import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.utiliti.ng";
const EVENT_API_BASE = '/api';

// Create axios instance with authentication
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const eventService = {
  // Get all events with pagination, search, and filters (admin)
  getAllEvents: async (params = {}) => {
    const {
      page = 1,
      limit = 10,
      search = '',
      status = '',
      category = '',
      eventType = '',
      vendorId = ''
    } = params;

    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (search) queryParams.append('search', search);
    if (status) queryParams.append('status', status);
    if (category) queryParams.append('category', category);
    if (eventType) queryParams.append('eventType', eventType);
    if (vendorId) queryParams.append('vendorId', vendorId);

    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/admin/events?${queryParams.toString()}`
    );

    return response.data;
  },

  // Get a specific event by ID
  getEventById: async (eventId) => {
    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/events/${eventId}`
    );
    return response.data;
  },

  // Get pending events for approval (admin)
  getPendingEvents: async (params = {}) => {
    const {
      page = 1,
      limit = 10,
      search = '',
      category = '',
      eventType = ''
    } = params;

    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (search) queryParams.append('search', search);
    if (category) queryParams.append('category', category);
    if (eventType) queryParams.append('eventType', eventType);

    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/admin/pending?${queryParams.toString()}`
    );

    return response.data;
  },

  // Approve an event (admin)
  approveEvent: async (eventId, approvalData = {}) => {
    const response = await axiosInstance.post(
      `${EVENT_API_BASE}/admin/events/${eventId}/approve`,
      approvalData
    );
    return response.data;
  },

  // Reject an event (admin)
  rejectEvent: async (eventId, rejectionData = {}) => {
    const response = await axiosInstance.post(
      `${EVENT_API_BASE}/admin/events/${eventId}/reject`,
      rejectionData
    );
    return response.data;
  },

  // Update event status (admin)
  updateEventStatus: async (eventId, status, reason = '') => {
    const response = await axiosInstance.patch(
      `${EVENT_API_BASE}/admin/events/${eventId}/status`,
      { status, reason }
    );
    return response.data;
  },

  // Toggle featured status (admin)
  toggleFeatured: async (eventId, isFeatured) => {
    const response = await axiosInstance.patch(
      `${EVENT_API_BASE}/admin/events/${eventId}/featured`,
      { isFeatured }
    );
    return response.data;
  },

  // Delete an event (admin)
  deleteEvent: async (eventId) => {
    const response = await axiosInstance.delete(
      `${EVENT_API_BASE}/admin/events/${eventId}`
    );
    return response.data;
  },

  // Get event statistics (admin)
  getEventStats: async () => {
    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/admin/events/stats`
    );

    return response.data;
  },

  // Get events by vendor
  getEventsByVendor: async (vendorId, params = {}) => {
    const {
      page = 1,
      limit = 10,
      status = ''
    } = params;

    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (status) queryParams.append('status', status);

    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/vendor/${vendorId}?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get event categories
  getEventCategories: async () => {
    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/categories`
    );
    return response.data;
  },

  // Get event types
  getEventTypes: async () => {
    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/types`
    );
    return response.data;
  },

  // Get event analytics
  getEventAnalytics: async (eventId) => {
    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/admin/events/${eventId}/analytics`
    );
    return response.data;
  },

  // Get event tickets
  getEventTickets: async (eventId, params = {}) => {
    const { page = 1, limit = 100, status = '' } = params;
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (status) queryParams.append('status', status);

    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/admin/events/${eventId}/tickets?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get event attendees
  getEventAttendees: async (eventId, params = {}) => {
    const { page = 1, limit = 100 } = params;
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);

    const response = await axiosInstance.get(
      `${EVENT_API_BASE}/admin/events/${eventId}/attendees?${queryParams.toString()}`
    );
    return response.data;
  },

  // Toggle event featured status (admin)
  toggleEventFeatured: async (eventId, isFeatured) => {
    const response = await axiosInstance.patch(
      `${EVENT_API_BASE}/admin/events/${eventId}/featured`,
      { isFeatured }
    );
    return response.data;
  }
};

// Error handling utility
export const handleEventApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    switch (status) {
      case 400:
        return data.message || 'Bad request. Please check your input.';
      case 401:
        return 'Unauthorized. Please log in again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Event not found.';
      case 409:
        return data.message || 'Conflict. This action cannot be completed.';
      case 422:
        return data.message || 'Validation error. Please check your input.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return data.message || `Error ${status}: Something went wrong.`;
    }
  } else if (error.request) {
    // Request was made but no response received
    return 'Network error. Please check your connection and try again.';
  } else {
    // Something else happened
    return error.message || 'An unexpected error occurred.';
  }
};

// Data transformation utilities
export const transformEventData = (data) => {
  if (Array.isArray(data)) {
    return data.map(event => ({
      ...event,
      id: event._id || event.id,
      startDate: event.startDate ? new Date(event.startDate) : null,
      endDate: event.endDate ? new Date(event.endDate) : null,
      createdAt: event.createdAt ? new Date(event.createdAt) : null,
      updatedAt: event.updatedAt ? new Date(event.updatedAt) : null,
      status: event.status || 'pending',
      category: event.category || 'Other',
      eventType: event.eventType || 'in-person',
      vendorName: event.vendor?.businessName || event.vendorName || 'Unknown Vendor',
      totalTickets: event.totalTickets || 0,
      soldTickets: event.soldTickets || 0,
      revenue: event.revenue || 0
    }));
  }

  return {
    ...data,
    id: data._id || data.id,
    startDate: data.startDate ? new Date(data.startDate) : null,
    endDate: data.endDate ? new Date(data.endDate) : null,
    createdAt: data.createdAt ? new Date(data.createdAt) : null,
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
    status: data.status || 'pending',
    category: data.category || 'Other',
    eventType: data.eventType || 'in-person',
    vendorName: data.vendor?.businessName || data.vendorName || 'Unknown Vendor',
    totalTickets: data.totalTickets || 0,
    soldTickets: data.soldTickets || 0,
    revenue: data.revenue || 0
  };
};

// Pagination utilities
export const createEventPaginationParams = (page, limit, filters = {}) => {
  return {
    page,
    limit,
    ...filters
  };
};

// Search and filter utilities
export const createEventSearchFilters = (searchTerm, statusFilter, categoryFilter, eventTypeFilter) => {
  const filters = {};

  if (searchTerm) filters.search = searchTerm;
  if (statusFilter) filters.status = statusFilter;
  if (categoryFilter) filters.category = categoryFilter;
  if (eventTypeFilter) filters.eventType = eventTypeFilter;

  return filters;
}; 